import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';
import './Register.css'; // Import the CSS styles

const Register = ({ asModal = false, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { signUp } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // If used as a modal, close on ESC
  useEffect(() => {
    if (!asModal) return;
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [asModal, onClose]);

  const handleRegister = async (event) => {
    event.preventDefault();

    if (!name || !email || !password) {
      setError(t('register.errorFill', 'Please complete all fields.'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('register.errorInvalidEmail', 'The email entered is not valid.'));
      return;
    }

    if (password.length < 6) {
      setError(t('register.passwordTooShort', 'Password must be at least 6 characters.'));
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await signUp(email, password, {
        username: name.trim(),
      });

      if (asModal && onClose) {
        onClose();
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.error_description || error.message || t('register.errorGeneric', 'Registration failed. Please try again.');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    asModal ? (
      <div className="modal-backdrop" role="dialog" aria-modal="true">
        <div
          className="modal-card auth-modal"
          role="document"
          style={{ width: 'min(400px, 92vw)', margin: '0 auto' }}
        >
          <button
            type="button"
            className="modal-close"
            aria-label={t('common.close', 'Close')}
            onClick={() => onClose && onClose()}
          >×</button>
          <div>
            <div className="register-header" style={{ marginBottom: '0.5rem' }}>
              <img src="/assets/cartolapro.svg" alt="CartolaPro logo" className="logo-fixed" style={{ display: 'block', margin: '0 auto', height: '48px' }} />
              <div className="register-intro" style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                <h2 style={{ margin: 0 }}>{t('register.title', 'Crear cuenta')}</h2>
                <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280' }}>
                  Analiza tus cartolas, detecta suscripciones y proyecta tus gastos en minutos.
                </p>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0.5rem 0 0',
                  display: 'inline-flex',
                  gap: '0.75rem',
                  color: '#475569',
                  fontSize: 13,
                  flexWrap: 'wrap',
                  justifyContent: 'center'
                }}>
                  <li>✓ Suscripciones automáticas</li>
                  <li>✓ Proyección mensual</li>
                  <li>✓ Soporte de múltiples cartolas</li>
                </ul>
              </div>
            </div>
            {error && <div className="register-error" style={{ color: 'red', marginBottom: '0.75rem' }}>{error}</div>}
            <form onSubmit={handleRegister} className="register-form auth-form">
              <div className="register-input-group">
                <label>{t('register.name', 'Name')}:</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="register-input" placeholder={t('register.namePlaceholder', 'Your name')} disabled={isLoading} />
              </div>
              <div className="register-input-group">
                <label>{t('register.email', 'Email')}:</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="register-input" placeholder={t('register.emailPlaceholder', 'you@example.com')} disabled={isLoading} />
              </div>
              <div className="register-input-group">
                <label>{t('register.password', 'Password')}:</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="register-input" placeholder={t('register.passwordPlaceholder', 'Password')} disabled={isLoading} />
              </div>
              <button
                type="submit"
                className="register-button"
                style={{
                  backgroundColor: '#1e40af',
                  color: 'white',
                  border: 'none',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#1e40af'}
                disabled={isLoading}
              >
                {isLoading ? t('register.creatingAccount', 'Creating Account...') : t('register.submit', 'Create Account')}
              </button>
            </form>
            <div className="register-footer-hint" style={{ marginTop: '0.5rem', fontSize: 12, color: '#64748b', textAlign: 'center' }}>
              {t('register.terms', 'Al registrarte aceptas nuestros términos y la política de privacidad.')}
              <div style={{ marginTop: 6 }}>
                {t('register.haveAccount', '¿Ya tienes cuenta?')}{' '}
                <button
                  style={{ fontSize: '12px', color: '#1d4ed8', textDecoration: 'none', fontWeight: 600, lineHeight: 1 }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (onClose) onClose();
                    const loginButton = document.querySelector('button[aria-label="Ingresar"]');
                    if (loginButton) loginButton.click();
                  }}
                >
                  {t('login.signIn', 'Inicia sesión')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div className="register-wrapper">
        <div className="register-container">
          <div className="register-header">
            <img src="/assets/cartolapro.svg" alt="CartolaPro logo" className="logo-fixed" style={{ display: 'block', margin: '0 auto', position: 'relative', top: '1rem', height: '64px', zIndex: 1000 }} />
            <div className="register-intro" style={{ textAlign: 'center', marginTop: '1rem' }}>
              <h2 style={{ margin: 0 }}>{t('register.title', 'Crear cuenta')}</h2>
              <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280' }}>
                Analiza tus cartolas, detecta suscripciones y proyecta tus gastos en minutos.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0.75rem 0 0', display: 'inline-flex', gap: '1rem', color: '#475569', fontSize: 14 }}>
                <li>✓ Suscripciones automáticas</li>
                <li>✓ Proyección mensual</li>
                <li>✓ Soporte de múltiples cartolas</li>
              </ul>
            </div>
          </div>
          {error && <div className="register-error" style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}
          <form onSubmit={handleRegister} className="register-form">
            <div className="register-input-group">
              <label>{t('register.name', 'Name')}:</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="register-input" placeholder={t('register.namePlaceholder', 'Your name')} disabled={isLoading} />
            </div>
            <div className="register-input-group">
              <label>{t('register.email', 'Email')}:</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="register-input" placeholder={t('register.emailPlaceholder', 'you@example.com')} disabled={isLoading} />
            </div>
            <div className="register-input-group">
              <label>{t('register.password', 'Password')}:</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="register-input" placeholder={t('register.passwordPlaceholder', 'Password')} disabled={isLoading} />
            </div>
            <button type="submit" className="register-button">{t('register.submit', 'Create Account')}</button>
          </form>
          <div className="register-footer-hint" style={{ marginTop: '0.75rem', fontSize: 12, color: '#64748b', textAlign: 'center' }}>
            {t('register.terms', 'Al registrarte aceptas nuestros términos y la política de privacidad.')}
            <div style={{ marginTop: 6 }}>
              {t('register.haveAccount', '¿Ya tienes cuenta?')}{' '}<a href="/login" style={{ fontSize: '12px', color: '#1d4ed8', textDecoration: 'none', fontWeight: 600, lineHeight: 1 }}>{t('login.signIn', 'Inicia sesión')}</a>
            </div>
          </div>
        </div>
      </div>
    )
  );
};

export default Register;